import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  RotateCcw,
  Check,
  ArrowRight,
  Heart,
  MapPin,
  ShieldCheck,
  Mail,
  CircleCheck,
  Search,
} from "lucide-react";
import { IMAGES } from "@/data/content";
import { useNoIndex } from "@/lib/useNoIndex";
import { useReelTimeline } from "@/components/demo/useReelTimeline";
import { DemoPlayerBar } from "@/components/demo/DemoPlayerBar";
import { DemoEndSlate } from "@/components/DemoEndSlate";

/**
 * DemoDonorStory — 60-second auto-playing walkthrough of the donor experience.
 *
 * Route: /demo/donor-story
 *
 * Third leg of the board-meeting narrative:
 *   1. /demo/story        — the student creates a campaign
 *   2. /demo/donor-story  — a donor discovers, decides, and gives  (this reel)
 *   3. /demo/admin-story  — the team verifies and disburses
 *
 * Acts:
 *   01  Browse verified students (editorial grid of campaigns)
 *   02  Land on Zohran's campaign page (real story, progress bar)
 *   03  Open the donation dialog (preset + custom + finish-the-goal)
 *   04  Three-checkbox 501(c)(3) attestation (the compliance moment)
 *   05  Payment succeeds (receipt confirmation, tax deductible note)
 *   06  End slate (yellow watermark, wordmark, donor payoff)
 */

const TOTAL_SECONDS = 60;

const ACTS = [
  {
    id: "01",
    startAt: 0,
    duration: 8,
    kicker: null,
    title: "Browse verified students.",
    subtitle:
      "Every profile below has cleared enrollment and eligibility checks. Every dollar is tax-deductible.",
  },
  {
    id: "02",
    startAt: 8,
    duration: 12,
    kicker: null,
    title: "Read their stories in their words.",
    subtitle: null,
  },
  {
    id: "03",
    startAt: 20,
    duration: 12,
    kicker: null,
    title: "Pick an amount, or finish the goal.",
    subtitle:
      "A 5% boost covers card fees so 100% of the gift funds student grants. Or top up whatever the campaign still needs.",
  },
  {
    id: "04",
    startAt: 32,
    duration: 16,
    kicker: "Compliance, compliance, My Rich Aunt is committed to compliance.",
    title: "Three checkboxes. Every donor. Every time.",
    subtitle:
      "Three attestations for any donation that names a preferred student. Nothing moves without all three checked.",
  },
  {
    id: "05",
    startAt: 48,
    duration: 6,
    kicker: null,
    title: "Gift confirmed. Tax receipt emailed.",
    subtitle:
      "501(c)(3) receipt lands in the donor's inbox before they close the tab.",
  },
  {
    id: "06",
    startAt: 54,
    duration: 6,
    kicker: null,
    title:
      "Real students. Real receipts.",
    subtitle: null,
  },
];

export default function DemoDonorStory() {
  useNoIndex();

  const timeline = useReelTimeline(ACTS, TOTAL_SECONDS);
  const { elapsed, ended, currentAct, actProgress, restart } = timeline;

  return (
    <main
      data-testid="demo-donor-story-page"
      className="relative min-h-screen overflow-hidden bg-sand text-ink"
    >
      {/* Warm ambient tint — matches the hero band's warm-sand mood */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, rgba(253,97,6,0.35) 0, transparent 45%), radial-gradient(circle at 80% 80%, rgba(45,180,49,0.30) 0, transparent 40%)",
        }}
      />
      {/* Repeating brand-mark pattern watermark — same treatment as the
          hero band uses (Hero.jsx). Tiled full-screen at 3.5% opacity
          with a multiply blend so it reads as a whisper of brand
          behind every scene, never competing with the acts. */}
      <div
        aria-hidden
        data-testid="demo-donor-story-pattern-watermark"
        className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-multiply"
        style={{
          backgroundImage: `url(${IMAGES.pattern})`,
          backgroundSize: "460px",
          backgroundRepeat: "repeat",
        }}
      />

      <header className="relative z-20 flex items-center justify-between px-6 py-5 lg:px-10">
        <Link
          to="/demo"
          data-testid="demo-donor-story-exit"
          className="text-[10px] font-semibold uppercase tracking-[0.28em] text-olive transition hover:text-ink"
        >
          ← back to /demo
        </Link>
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-olive">
          <span>Board demo</span>
          <span className="h-1 w-1 rounded-full bg-ink/25" />
          <span>Donor experience</span>
          <span className="h-1 w-1 rounded-full bg-ink/25" />
          <span data-testid="demo-donor-story-scene-index">
            {currentAct.id} / {String(ACTS.length).padStart(2, "0")}
          </span>
        </div>
      </header>

      <section className="relative z-10 mx-auto flex min-h-[calc(100vh-9rem)] max-w-7xl items-center px-6 pb-24 lg:px-10">
        <AnimatePresence mode="wait">
          {currentAct.id === "06" ? (
            <motion.div
              key="end-slate"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="w-full"
              data-testid={`demo-donor-story-act-${currentAct.id}`}
            >
              <DemoEndSlate onReplay={restart} />
            </motion.div>
          ) : (
            <motion.div
              key={currentAct.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="grid w-full grid-cols-1 items-center gap-8 lg:grid-cols-[1.35fr_1fr] lg:gap-14"
              data-testid={`demo-donor-story-act-${currentAct.id}`}
            >
              <SceneVisual act={currentAct} actProgress={actProgress} />
              <SceneCopy act={currentAct} ended={ended} onReplay={restart} />
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      <DemoPlayerBar acts={ACTS} totalSeconds={TOTAL_SECONDS} timeline={timeline} testid="demo-donor-story" />

      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 z-10 h-[3px] bg-ink/10"
      >
        <div
          data-testid="demo-donor-story-progress"
          className="h-full bg-yellow"
          style={{
            width: `${Math.min(100, (elapsed / TOTAL_SECONDS) * 100)}%`,
            transition: "width 60ms linear",
          }}
        />
      </div>

      <p className="sr-only" aria-live="polite">
        {currentAct.title}
      </p>
    </main>
  );
}

/* ─── Shared primitives ─────────────────────────────────────────────── */

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

const BrowserChrome = ({ children, url = "myrichaunt.org" }) => (
  <div className="relative w-full overflow-hidden rounded-2xl bg-bone text-ink shadow-[0_40px_100px_-40px_rgba(0,0,0,0.55)]">
    <div className="flex items-center gap-1.5 border-b border-border bg-sand/70 px-4 py-3">
      <span className="h-2.5 w-2.5 rounded-full bg-ink/20" />
      <span className="h-2.5 w-2.5 rounded-full bg-ink/20" />
      <span className="h-2.5 w-2.5 rounded-full bg-ink/20" />
      <span className="ml-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">
        {url}
      </span>
    </div>
    {children}
  </div>
);

/* ─── Scene switch ──────────────────────────────────────────────────── */

const SceneVisual = ({ act, actProgress }) => {
  switch (act.id) {
    case "01": return <ActBrowse actProgress={actProgress} />;
    case "02": return <ActCampaignDetail actProgress={actProgress} />;
    case "03": return <ActDonateAmount actProgress={actProgress} />;
    case "04": return <ActAttestation actProgress={actProgress} />;
    case "05": return <ActSuccess actProgress={actProgress} />;
    default: return null;
  }
};

const SceneCopy = ({ act, ended, onReplay }) => (
  <div className="relative">
    {act.kicker && (
      <p
        data-testid={`demo-donor-story-kicker-${act.id}`}
        className="text-[11px] font-semibold uppercase tracking-[0.32em] text-tangerine"
      >
        {act.kicker}
      </p>
    )}
    <h1
      data-testid={`demo-donor-story-title-${act.id}`}
      className={`${act.kicker ? "mt-4" : ""} font-serif text-3xl leading-[1.05] tracking-tight text-ink lg:text-5xl`}
    >
      {act.title}
    </h1>
    {act.subtitle && (
      <p
        data-testid={`demo-donor-story-subtitle-${act.id}`}
        className="mt-5 max-w-xl text-[16px] leading-relaxed text-ink/70"
      >
        {act.subtitle}
      </p>
    )}
    {(act.id === "06" || ended) && (
      <div className="mt-9 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onReplay}
          data-testid="demo-donor-story-replay"
          className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-sand transition hover:bg-forest"
        >
          <RotateCcw className="h-4 w-4" strokeWidth={2.2} />
          Play again
        </button>
        <Link
          to="/demo"
          data-testid="demo-donor-story-back-to-demo"
          className="inline-flex items-center gap-2 rounded-full border border-ink/25 bg-bone px-6 py-3 text-sm font-semibold text-ink transition hover:bg-beige"
        >
          Explore the platform
          <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </Link>
      </div>
    )}
  </div>
);

/* ─── Act 01: Browse verified students ──────────────────────────────── */

const CAMPAIGNS_PREVIEW = [
  { name: "Zohran M.", school: "UC Berkeley '27", category: "Study abroad", raised: 1240, goal: 2000, img: "group" },
  { name: "Maya L.",   school: "Stanford '27",    category: "Textbooks",    raised: 185,  goal: 285,  img: "student1" },
  { name: "Andre W.",  school: "Morehouse '28",   category: "Winter gear",  raised: 110,  goal: 220,  img: "student2" },
];

const ActBrowse = ({ actProgress }) => {
  const cardVisible = (i) => actProgress > 0.1 + i * 0.14;
  return (
    <BrowserChrome url="myrichaunt.org · campaigns">
      <div className="p-5 lg:p-6">
        <div className="mb-4 flex items-baseline justify-between border-b border-border pb-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-tangerine">
              Meet the students
            </p>
            <h3 className="mt-1 font-serif text-2xl leading-tight text-ink lg:text-3xl">
              People you'd back if you knew them.
            </h3>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-sand/40 px-3 py-1.5 text-[11px] text-olive">
            <Search className="h-3.5 w-3.5" strokeWidth={2} />
            Filter by category
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3.5">
          {CAMPAIGNS_PREVIEW.map((c, i) => {
            const visible = cardVisible(i);
            return (
              <motion.div
                key={c.name}
                initial={{ opacity: 0, y: 14 }}
                animate={{
                  opacity: visible ? 1 : 0,
                  y: visible ? 0 : 14,
                }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className={`overflow-hidden rounded-md border border-border ${i === 0 ? "ring-2 ring-cobalt/40" : ""}`}
              >
                <div className="relative aspect-[4/5] w-full bg-ink">
                  <img
                    src={IMAGES[c.img] || IMAGES.group}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent"
                  />
                  <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full border-2 border-grass bg-bone px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.18em] text-grass">
                    <Check className="h-2.5 w-2.5" strokeWidth={2.8} /> verified
                  </span>
                </div>
                <div className="p-3">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-olive">
                    {c.category}
                  </p>
                  <p className="mt-0.5 font-serif text-lg leading-tight text-ink">{c.name}</p>
                  <p className="text-[10px] text-olive">{c.school}</p>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-ink/10">
                    <div
                      className="h-full bg-tangerine"
                      style={{ width: `${(c.raised / c.goal) * 100}%` }}
                    />
                  </div>
                  <p className="mt-1.5 text-[10px] font-mono text-ink">
                    ${c.raised.toLocaleString()} of ${c.goal.toLocaleString()}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </BrowserChrome>
  );
};

/* ─── Act 02: Campaign detail page ──────────────────────────────────── */

const ActCampaignDetail = ({ actProgress }) => {
  const t = actProgress;
  return (
    <BrowserChrome url="myrichaunt.org/campaign/zohran">
      <div className="grid grid-cols-1 gap-5 p-5 lg:grid-cols-[1.35fr_1fr] lg:p-6">
        <div>
          <div className="relative aspect-[16/10] w-full overflow-hidden rounded-md">
            <img src={IMAGES.group} alt="" className="h-full w-full object-cover" />
            <span aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent" />
            <motion.div
              initial={{ opacity: 0, scale: 0.7, rotate: -14 }}
              animate={{ opacity: t > 0.05 ? 1 : 0, scale: t > 0.05 ? 1 : 0.7, rotate: t > 0.05 ? -8 : -14 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full border-2 border-grass bg-bone px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.2em] text-grass shadow-md"
            >
              <Check className="h-3 w-3" strokeWidth={2.8} /> Verified &amp; live
            </motion.div>
          </div>

          <div className="mt-4">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-tangerine">Study abroad</span>
            <h3 className="mt-1 font-serif text-2xl leading-tight text-ink lg:text-3xl">Nairobi, or bust.</h3>
            <p className="mt-2 flex items-center gap-1.5 text-[12px] text-olive">
              <MapPin className="h-3 w-3" strokeWidth={2} />
              Zohran M. · UC Berkeley '27, International Relations
            </p>
            <p className="mt-3 text-[13px] leading-relaxed text-ink/85">
              A semester studying in Nairobi would change how I see the world, and my future. A grant from My Rich Aunt would cover my program fee, visa, and travel so I can focus on the work instead of the bill.
            </p>
          </div>
        </div>

        <div>
          <div className="rounded-md border border-border bg-sand/40 p-5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">Progress</span>
            <p className="mt-2 font-serif text-4xl leading-none text-ink">
              ${Math.round(1240 + clamp(t, 0, 1) * 260).toLocaleString()}
            </p>
            <p className="mt-1 text-[12px] text-olive">
              raised of <span className="font-semibold text-ink">$2,000</span> goal
            </p>
            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-ink/10">
              <div
                className="h-full bg-tangerine"
                style={{ width: `${((1240 + t * 260) / 2000) * 100}%`, transition: "width 120ms linear" }}
              />
            </div>
            <motion.button
              initial={{ scale: 1 }}
              animate={{ scale: t > 0.75 ? [1, 1.04, 1] : 1 }}
              transition={{ duration: 0.5, repeat: t > 0.75 ? Infinity : 0 }}
              type="button"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-ink px-5 py-2.5 text-[12px] font-semibold text-sand"
            >
              <Heart className="h-3.5 w-3.5" strokeWidth={2} />
              Donate now
            </motion.button>
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
};

/* ─── Act 03: Donation amount + prefs dialog ────────────────────────── */

// Zohran campaign values (matches CAMPAIGNS_PREVIEW): raised 1240 of 2000 goal
const ZOHRAN_RAISED = 1240;
const ZOHRAN_GOAL = 2000;
const ZOHRAN_REMAINING = ZOHRAN_GOAL - ZOHRAN_RAISED; // $760

const ActDonateAmount = ({ actProgress }) => {
  const t = clamp(actProgress, 0, 1);
  const PRESETS = [25, 50, 100, 250];
  const OTHER_TYPED = 500;

  // Phase machine — cycles the animation through 4 states so board
  // members see: preset → larger preset → Other with typed amount →
  // "finish the goal" (which is the punchline for this scene).
  //   phase 0: preset $50 highlighted
  //   phase 1: preset $250 highlighted
  //   phase 2: "Other" selected, typing animates in
  //   phase 3: "Finish the goal" — $760 remaining highlighted
  let phase = 0;
  if (t > 0.72) phase = 3;
  else if (t > 0.48) phase = 2;
  else if (t > 0.26) phase = 1;

  // Preset selection index (only meaningful in phases 0 and 1)
  const selectedPresetIdx = phase === 0 ? 1 : phase === 1 ? 3 : -1;
  const otherActive = phase === 2;
  const fulfillActive = phase === 3;

  // Progressive typing for the "Other" fill-in (only during phase 2)
  const typingProgress = clamp((t - 0.48) / 0.24, 0, 1);
  const typedValue = otherActive
    ? Math.round(OTHER_TYPED * typingProgress)
    : phase > 2
      ? OTHER_TYPED
      : 0;

  // Which numeric value drives the running total
  const baseAmount = phase === 0
    ? PRESETS[1]
    : phase === 1
      ? PRESETS[3]
      : phase === 2
        ? typedValue
        : ZOHRAN_REMAINING;

  const totalWithBoost = baseAmount * 1.05;
  const boostOn = true;

  return (
    <BrowserChrome url="myrichaunt.org/campaign/zohran · donate">
      <div className="p-5 lg:p-6">
        {/* Header — donations flow to My Rich Aunt in support of the student */}
        <div className="flex items-center justify-between border-b border-border pb-3">
          <h3
            data-testid="demo-donor-story-donate-headline"
            className="font-serif text-xl leading-tight text-ink lg:text-2xl"
          >
            Support Zohran M's Campaign.
          </h3>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-grass/12 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-grass ring-1 ring-inset ring-grass/30">
            <ShieldCheck className="h-3 w-3" strokeWidth={2.4} /> Tax-deductible
          </span>
        </div>

        {/* Preset amount picker */}
        <div className="mt-5">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink">Pick an amount</p>
          <div className="mt-2 grid grid-cols-4 gap-2">
            {PRESETS.map((amt, i) => {
              const active = i === selectedPresetIdx;
              return (
                <motion.button
                  key={amt}
                  type="button"
                  animate={{
                    borderColor: active ? "rgb(253,97,6)" : "rgb(230,217,195)",
                    backgroundColor: active ? "rgba(253,97,6,0.10)" : "rgba(237,228,211,0.4)",
                    color: active ? "rgb(253,97,6)" : "rgb(26,23,20)",
                  }}
                  transition={{ duration: 0.28 }}
                  className="rounded-md border py-2.5 text-center font-serif text-lg"
                >
                  ${amt}
                </motion.button>
              );
            })}
          </div>

          {/* "Other" — custom amount with animated fill-in */}
          <motion.div
            animate={{
              borderColor: otherActive ? "rgb(253,97,6)" : "rgb(230,217,195)",
              backgroundColor: otherActive ? "rgba(253,97,6,0.08)" : "rgba(237,228,211,0.4)",
            }}
            transition={{ duration: 0.28 }}
            className="mt-2 flex items-center justify-between rounded-md border px-3 py-2.5"
          >
            <span
              className={`text-[13px] font-semibold ${otherActive ? "text-tangerine" : "text-ink"}`}
            >
              Other
            </span>
            <div className="flex items-center gap-1 font-serif text-lg text-ink">
              <span className="text-olive">$</span>
              <motion.span
                animate={{ opacity: phase >= 2 ? 1 : 0.4 }}
                className="min-w-[3ch] text-right tabular-nums"
              >
                {phase >= 2 ? typedValue : "—"}
              </motion.span>
              {otherActive && (
                <motion.span
                  aria-hidden
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 0.7, repeat: Infinity }}
                  className="ml-0.5 inline-block h-4 w-[2px] bg-tangerine"
                />
              )}
            </div>
          </motion.div>

          {/* Finish-the-goal shortcut — encourages donors to close the gap */}
          <motion.button
            type="button"
            animate={{
              borderColor: fulfillActive ? "rgb(253,97,6)" : "rgb(230,217,195)",
              backgroundColor: fulfillActive ? "rgba(253,97,6,0.10)" : "rgba(237,228,211,0.4)",
            }}
            transition={{ duration: 0.28 }}
            className="mt-2 flex w-full items-center justify-between rounded-md border px-3 py-2.5 text-left"
            data-testid="demo-donor-story-fulfill-goal"
          >
            <div>
              <p
                className={`text-[13px] font-semibold ${fulfillActive ? "text-tangerine" : "text-ink"}`}
              >
                Donate enough to fulfill this request
              </p>
              <p className="mt-0.5 text-[10.5px] leading-snug text-olive">
                $760 remaining to reach Zohran's goal.
              </p>
            </div>
            <span
              className={`font-serif text-lg ${fulfillActive ? "text-tangerine" : "text-ink"}`}
            >
              $760
            </span>
          </motion.button>
        </div>

        {/* Boost card */}
        <div className="mt-4 rounded-md border border-tangerine/25 bg-tangerine/[0.05] p-3.5">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 text-[13px] font-semibold text-ink">
                <Heart className="h-3.5 w-3.5 text-tangerine" strokeWidth={2} fill="currentColor" />
                Boost 5% to cover fees
              </p>
              <p className="mt-1 text-[11.5px] leading-snug text-olive">
                So 100% of your gift funds student grants.
              </p>
            </div>
            <motion.span
              animate={{ backgroundColor: boostOn ? "rgb(253,97,6)" : "rgba(26,23,20,0.15)" }}
              transition={{ duration: 0.28 }}
              className="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full px-0.5"
            >
              <motion.span
                animate={{ x: boostOn ? 16 : 0 }}
                transition={{ duration: 0.28 }}
                className="inline-block h-4 w-4 rounded-full bg-bone shadow"
              />
            </motion.span>
          </div>
        </div>

        {/* Running total */}
        <div className="mt-5 flex items-baseline justify-between border-t border-border pt-3">
          <span className="text-[11px] uppercase tracking-[0.18em] text-olive">Total today</span>
          <span className="font-serif text-3xl text-ink" data-testid="demo-donor-story-total">
            ${totalWithBoost.toFixed(2)}
          </span>
        </div>
      </div>
    </BrowserChrome>
  );
};

/* ─── Act 04: The three-checkbox 501(c)(3) attestation ──────────────── */

const ActAttestation = ({ actProgress }) => {
  const t = actProgress;
  const acks = [
    {
      id: "preference",
      color: "cobalt",
      boxFill: "rgb(3,34,244)",
      wrapCls: "border-cobalt/30 bg-cobalt/[0.04]",
      copyPrefix: "I understand my donation supports ",
      copyMid: "My Rich Aunt",
      copyPost: "'s supplemental educational grant program. My preference above is a non-binding indication of which student I hope will be funded; final grant decisions rest with My Rich Aunt.",
      checkAt: 0.14,
    },
    {
      id: "dependent",
      color: "tangerine",
      boxFill: "rgb(253,97,6)",
      wrapCls: "border-tangerine/30 bg-tangerine/[0.04]",
      copyPrefix: "I confirm ",
      copyMid: "Zohran M.",
      copyPost: " is not claimed as a dependent on my federal or state income tax returns, and is not an immediate family member of mine.",
      checkAt: 0.36,
    },
    {
      id: "quid",
      color: "scarlet",
      boxFill: "rgb(232,66,29)",
      wrapCls: "border-scarlet/30 bg-scarlet/[0.04]",
      copyPrefix: "I am not receiving any ",
      copyMid: "goods, services, or personal benefit",
      copyPost: " in return for this contribution.",
      checkAt: 0.58,
    },
  ];
  const allChecked = t > 0.82;
  const checkedCount = acks.filter((a) => t > a.checkAt).length;

  return (
    <BrowserChrome url="myrichaunt.org/campaign/zohran · attestation">
      <div className="p-5 lg:p-6">
        <div className="mb-3 flex items-baseline justify-end border-b border-border pb-3">
          <motion.span
            animate={{
              backgroundColor: allChecked ? "rgba(45,180,49,0.15)" : "rgba(45,180,49,0)",
              color: allChecked ? "rgb(45,180,49)" : "rgb(115,116,88)",
            }}
            transition={{ duration: 0.35 }}
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]"
          >
            {allChecked ? (
              <>
                <CircleCheck className="h-3 w-3" strokeWidth={2.4} /> All 3 required · complete
              </>
            ) : (
              <>{checkedCount} of 3 required</>
            )}
          </motion.span>
        </div>

        <p className="mb-3 text-[12px] leading-relaxed text-ink">
          Your gift is made to <span className="font-semibold">My Rich Aunt</span>, a 501(c)(3) nonprofit, and is fully tax-deductible.
        </p>

        <div className="space-y-3">
          {acks.map((row) => {
            const checked = t > row.checkAt;
            return (
              <div key={row.id} className={`flex items-start gap-3 rounded-md border p-4 ${row.wrapCls}`}>
                <motion.span
                  animate={{
                    backgroundColor: checked ? row.boxFill : "rgb(255,255,255)",
                    borderColor: checked ? row.boxFill : "rgb(200,190,170)",
                  }}
                  transition={{ duration: 0.3 }}
                  className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border-2"
                >
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: checked ? 1 : 0 }}
                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Check className="h-3 w-3 text-bone" strokeWidth={3.2} />
                  </motion.span>
                </motion.span>
                <span className="text-[12.5px] leading-relaxed text-ink">
                  {row.copyPrefix}
                  <span className="font-semibold">{row.copyMid}</span>
                  {row.copyPost}
                </span>
              </div>
            );
          })}
        </div>

        <motion.div
          animate={{ opacity: allChecked ? 1 : 0.4 }}
          transition={{ duration: 0.4 }}
          className="mt-4"
        >
          <span className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-4 py-2.5 text-[12px] font-semibold text-sand">
            Continue to secure checkout
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.4} />
          </span>
        </motion.div>
      </div>
    </BrowserChrome>
  );
};

/* ─── Act 05: Payment success ───────────────────────────────────────── */

const ActSuccess = ({ actProgress }) => {
  const t = actProgress;
  return (
    <BrowserChrome url="myrichaunt.org/donation/success">
      <div className="flex flex-col items-center px-5 py-8 lg:px-6 lg:py-10">
        <motion.span
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: t > 0.05 ? 1 : 0.5, opacity: t > 0.05 ? 1 : 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-grass/15 text-grass"
        >
          <Check className="h-8 w-8" strokeWidth={2.4} />
        </motion.span>

        <motion.h3
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: t > 0.2 ? 1 : 0, y: t > 0.2 ? 0 : 8 }}
          transition={{ duration: 0.5 }}
          className="mt-5 font-serif text-3xl leading-tight text-ink lg:text-4xl"
        >
          Thank you.
        </motion.h3>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: t > 0.35 ? 1 : 0 }}
          transition={{ duration: 0.4 }}
          className="mt-2 text-center text-[13px] text-olive"
        >
          $52.50 to My Rich Aunt · Preference: Zohran M. · Card ending 4242
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: t > 0.55 ? 1 : 0, y: t > 0.55 ? 0 : 8 }}
          transition={{ duration: 0.5 }}
          className="mt-5 flex items-center gap-2 rounded-full bg-yellow/25 px-4 py-2 text-[12px] font-semibold text-ink ring-1 ring-inset ring-yellow"
        >
          <Mail className="h-3.5 w-3.5 text-tangerine" strokeWidth={2} />
          Tax receipt sent to your inbox
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: t > 0.75 ? 1 : 0 }}
          transition={{ duration: 0.4 }}
          className="mt-6 max-w-md text-center text-[12px] leading-relaxed text-olive"
        >
          Grant disburses in weekly installments.
        </motion.p>
      </div>
    </BrowserChrome>
  );
};

/* ─── End of file — Act 06 is rendered via <DemoEndSlate /> at the top-level
       conditional in the reel's <section>. See imports above. ─────────── */
